import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Group,
    Loader,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
    IconCheck,
    IconCreditCard,
    IconDownload,
    IconEye,
    IconFileSpreadsheet,
    IconHotelService,
    IconRefresh,
    IconSearch,
    IconX,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";

import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { getEvents, type EventItem } from "../../../api/shiftsApi";
import {
    approveHotelBooking,
    getHotelBookingsForEvent,
    HotelBookingDto,
    markHotelBookingPaid,
    rejectHotelBooking,
    createHotelPaymentLink
} from "../../../api/hotelBookingApi";

type RoomingProgress = {
    totalGuests: number;
    filledGuests: number;
    missingGuests: number;
};

export default function HotelBookingAdminPage() {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const [events, setEvents] = useState<EventItem[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

    const [bookings, setBookings] = useState<HotelBookingDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState<number | null>(null);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [hotelFilter, setHotelFilter] = useState<string>("all");
    const [onlyMissingRooming, setOnlyMissingRooming] = useState(false);

    const [viewOpen, setViewOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<HotelBookingDto | null>(null);

    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    function roomMatchesDate(room: any) {
        if (!dateFrom && !dateTo) return true;

        const checkIn = room.checkInDate ? new Date(room.checkInDate) : null;
        const checkOut = room.checkOutDate ? new Date(room.checkOutDate) : null;

        if (!checkIn || !checkOut) return false;

        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;

        if (from && checkOut < from) return false;
        if (to && checkIn > to) return false;

        return true;
    }

    const eventSelectData = useMemo(
        () =>
            events.map((e) => ({
                value: String(e.eventID),
                label: e.eventName || `Event ${e.eventID}`,
            })),
        [events]
    );

    const hotelSelectData = useMemo(() => {
        const hotels = new Map<string, string>();

        bookings.forEach((booking) => {
            const value = String(booking.hotelID);
            const label = booking.hotelName || `Hotel ${booking.hotelID}`;
            hotels.set(value, label);
        });

        return [
            { value: "all", label: "All hotels" },
            ...Array.from(hotels.entries())
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([value, label]) => ({ value, label })),
        ];
    }, [bookings]);

    const filteredBookings = useMemo(() => {
        const q = search.trim().toLowerCase();

        return bookings.filter((booking) => {
            const progress = getRoomingProgress(booking);

            const matchesSearch =
                !q ||
                String(booking.hotelBookingID).includes(q) ||
                booking.hotelName?.toLowerCase().includes(q) ||
                booking.country?.toLowerCase().includes(q) ||
                booking.contactName?.toLowerCase().includes(q) ||
                booking.contactEmail?.toLowerCase().includes(q);

            const matchesStatus =
                statusFilter === "all" ||
                normalizeStatus(booking.status) === normalizeStatus(statusFilter);

            const matchesHotel =
                hotelFilter === "all" || String(booking.hotelID) === hotelFilter;

            const matchesMissingRooming =
                !onlyMissingRooming || progress.missingGuests > 0;

            return matchesSearch && matchesStatus && matchesHotel && matchesMissingRooming;
        });
    }, [bookings, search, statusFilter, hotelFilter, onlyMissingRooming]);

    const filteredRooms = useMemo(() => {
        return filteredBookings.flatMap((booking) =>
            (booking.rooms ?? [])
                .filter((room) => roomMatchesDate(room))
                .map((room) => ({
                    bookingID: booking.hotelBookingID,
                    paymentReference: booking.paymentReference || "",
                    hotel: booking.hotelName || `Hotel ${booking.hotelID}`,
                    country: booking.country || "",
                    contactName: booking.contactName || "",
                    contactEmail: booking.contactEmail || "",
                    status: booking.status,
                    roomType: room.roomTypeName || "Ukendt",
                    checkIn: room.checkInDate,
                    checkOut: room.checkOutDate,
                    guestsCount: room.guestsCount ?? 0,
                    pricePerNight: room.pricePerNight ?? 0,
                    totalPrice: room.totalPrice ?? 0,
                }))
        );
    }, [filteredBookings, dateFrom, dateTo]);

    const roomTypeStats = useMemo(() => {
        const map = new Map<string, number>();

        filteredRooms.forEach((room) => {
            map.set(room.roomType, (map.get(room.roomType) ?? 0) + 1);
        });

        return Array.from(map.entries())
            .map(([roomType, count]) => ({ roomType, count }))
            .sort((a, b) => b.count - a.count);
    }, [filteredRooms]);

    const stats = useMemo(() => {
        const totalBookings = filteredBookings.length;

        const totalRooms = filteredRooms.length;

        const totalGuests = filteredBookings.reduce(
            (sum, booking) => sum + getRoomingProgress(booking).totalGuests,
            0
        );

        const missingGuests = filteredBookings.reduce(
            (sum, booking) => sum + getRoomingProgress(booking).missingGuests,
            0
        );

        const awaitingPayment = filteredBookings.filter(
            (booking) => normalizeStatus(booking.status) === "awaitingpayment"
        ).length;

        const pendingApproval = filteredBookings.filter(
            (booking) => normalizeStatus(booking.status) === "pendingapproval"
        ).length;

        const revenue = filteredBookings.reduce(
            (sum, booking) => sum + (booking.totalPrice || 0),
            0
        );

        return {
            totalBookings,
            totalRooms,
            totalGuests,
            missingGuests,
            awaitingPayment,
            pendingApproval,
            revenue,
        };
    }, [filteredBookings, filteredRooms]);

    useEffect(() => {
        async function loadEvents() {
            try {
                const data = await getEvents(apiUrl);
                setEvents(data);
            } catch {
                notifications.show({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente events",
                });
            }
        }

        loadEvents();
    }, [apiUrl]);

    useEffect(() => {
        if (!selectedEventId) {
            setBookings([]);
            return;
        }

        loadBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);

    async function loadBookings() {
        if (!selectedEventId) return;

        setLoading(true);
        try {
            const data = await getHotelBookingsForEvent(apiUrl, selectedEventId);
            setBookings(Array.isArray(data) ? data : []);
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke hente bookinger",
            });
            setBookings([]);
        } finally {
            setLoading(false);
        }
    }

    function normalizeStatus(status?: string | null) {
        return (status || "").trim().replace(/\s+/g, "").toLowerCase();
    }

    function formatMoney(value: number) {
        return new Intl.NumberFormat("da-DK", {
            style: "currency",
            currency: "DKK",
        }).format(value || 0);
    }

    function formatDate(value?: string | null) {
        if (!value) return "-";
        return new Date(value).toLocaleDateString("da-DK");
    }

    function formatDateTime(value?: string | null) {
        if (!value) return "-";
        return new Date(value).toLocaleString("da-DK");
    }

    function getStatusColor(status: string) {
        switch (normalizeStatus(status)) {
            case "pendingapproval":
                return "yellow";
            case "awaitingpayment":
                return "blue";
            case "paid":
                return "green";
            case "roomassignmentopen":
                return "violet";
            case "completed":
                return "green";
            case "rejected":
                return "red";
            case "cancelled":
            case "paymentexpired":
                return "gray";
            default:
                return "gray";
        }
    }

    function getStatusText(status: string) {
        switch (normalizeStatus(status)) {
            case "pendingapproval":
                return "Pending approval";
            case "awaitingpayment":
                return "Awaiting payment";
            case "paid":
                return "Paid";
            case "roomassignmentopen":
                return "Rooming list open";
            case "completed":
                return "Completed";
            case "rejected":
                return "Rejected";
            case "cancelled":
                return "Cancelled";
            case "paymentexpired":
                return "Payment expired";
            default:
                return status;
        }
    }

    function getRoomingProgress(booking: HotelBookingDto): RoomingProgress {
        const rooms = booking.rooms ?? [];

        const totalGuests = rooms.reduce((sum, room) => {
            return sum + (room.guestsCount ?? 0);
        }, 0);

        const filledGuests = rooms.reduce((sum, room) => {
            const filled = room.guests?.filter((guest) => guest.personName?.trim()).length ?? 0;
            return sum + filled;
        }, 0);

        return {
            totalGuests,
            filledGuests,
            missingGuests: Math.max(0, totalGuests - filledGuests),
        };
    }

    function openView(booking: HotelBookingDto) {
        setSelectedBooking(booking);
        setViewOpen(true);
    }

    async function approveBooking(booking: HotelBookingDto) {
        if (!window.confirm(`Approve booking #${booking.hotelBookingID}?`)) return;

        setSavingId(booking.hotelBookingID);
        try {
            await approveHotelBooking(apiUrl, booking.hotelBookingID);
            notifications.show({
                color: "green",
                title: "Approved",
                message: `Booking #${booking.hotelBookingID} approved`,
            });
            await loadBookings();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke godkende booking",
            });
        } finally {
            setSavingId(null);
        }
    }

    async function rejectBooking(booking: HotelBookingDto) {
        if (!window.confirm(`Reject booking #${booking.hotelBookingID}?`)) return;

        setSavingId(booking.hotelBookingID);
        try {
            await rejectHotelBooking(apiUrl, booking.hotelBookingID);
            notifications.show({
                color: "green",
                title: "Rejected",
                message: `Booking #${booking.hotelBookingID} rejected`,
            });
            await loadBookings();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke afvise booking",
            });
        } finally {
            setSavingId(null);
        }
    }

    async function markPaid(booking: HotelBookingDto) {
        if (!window.confirm(`Mark booking #${booking.hotelBookingID} as paid?`)) return;

        setSavingId(booking.hotelBookingID);
        try {
            await markHotelBookingPaid(apiUrl, booking.hotelBookingID);
            notifications.show({
                color: "green",
                title: "Paid",
                message: `Booking #${booking.hotelBookingID} marked as paid`,
            });
            await loadBookings();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke markere som betalt",
            });
        } finally {
            setSavingId(null);
        }
    }

    async function createPaymentLink(booking: HotelBookingDto) {
        setSavingId(booking.hotelBookingID);

        try {
            const result = await createHotelPaymentLink(apiUrl, booking.hotelBookingID);

            notifications.show({
                color: "green",
                title: "Payment link created",
                message: `Payment link created for booking #${booking.hotelBookingID}`,
            });

            if (result.paymentLink) {
                await navigator.clipboard.writeText(result.paymentLink);
                notifications.show({
                    color: "blue",
                    title: "Copied",
                    message: "Payment link copied to clipboard",
                });
            }

            await loadBookings();
        } catch (e: any) {
            notifications.show({
                color: "red",
                title: "Fejl",
                message: e?.message || "Kunne ikke oprette payment link",
            });
        } finally {
            setSavingId(null);
        }
    }

    function exportBookingsToExcel() {
        if (filteredBookings.length === 0) {
            notifications.show({
                color: "yellow",
                title: "Ingen data",
                message: "Der er ingen bookinger at eksportere.",
            });
            return;
        }

        const rows = filteredBookings.map((booking) => {
            const progress = getRoomingProgress(booking);

            return {
                BookingID: booking.hotelBookingID,
                PaymentReference: booking.paymentReference || "",
                Hotel: booking.hotelName || `Hotel ${booking.hotelID}`,
                Status: getStatusText(booking.status),
                Country: booking.country || "",
                ContactName: booking.contactName || "",
                ContactEmail: booking.contactEmail || "",
                Rooms: booking.rooms?.length ?? 0,
                TotalGuests: progress.totalGuests,
                FilledGuests: progress.filledGuests,
                MissingGuests: progress.missingGuests,
                TotalPrice: booking.totalPrice || 0,
                CreatedAt: formatDateTime(booking.createdAt),
                RoomingDeadline: formatDateTime(booking.roomingDeadline),
            };
        });

        downloadExcel(rows, "HotelBookings", "hotel-bookings.xlsx");
    }

    function exportRoomingListToExcel() {
        const rows: any[] = [];

        filteredBookings.forEach((booking) => {
            booking.rooms?.forEach((room, roomIndex) => {
                const guests = room.guests ?? [];

                if (guests.length === 0) {
                    for (let i = 0; i < (room.guestsCount ?? 0); i++) {
                        rows.push({
                            BookingID: booking.hotelBookingID,
                            Hotel: booking.hotelName || `Hotel ${booking.hotelID}`,
                            Status: getStatusText(booking.status),
                            Country: booking.country || "",
                            ContactName: booking.contactName || "",
                            ContactEmail: booking.contactEmail || "",
                            RoomNo: roomIndex + 1,
                            RoomType: room.roomTypeName || "",
                            CheckIn: formatDate(room.checkInDate),
                            CheckOut: formatDate(room.checkOutDate),
                            GuestNo: i + 1,
                            GuestName: "",
                            Function: "",
                        });
                    }

                    return;
                }

                guests.forEach((guest, guestIndex) => {
                    rows.push({
                        BookingID: booking.hotelBookingID,
                        Hotel: booking.hotelName || `Hotel ${booking.hotelID}`,
                        Status: getStatusText(booking.status),
                        Country: booking.country || "",
                        ContactName: booking.contactName || "",
                        ContactEmail: booking.contactEmail || "",
                        RoomNo: roomIndex + 1,
                        RoomType: room.roomTypeName || "",
                        CheckIn: formatDate(room.checkInDate),
                        CheckOut: formatDate(room.checkOutDate),
                        GuestNo: guestIndex + 1,
                        GuestName: guest.personName || "",
                        Function: (guest as any).functionName || (guest as any).role || "",
                    });
                });

                const missingCount = Math.max(0, (room.guestsCount ?? 0) - guests.length);

                for (let i = 0; i < missingCount; i++) {
                    rows.push({
                        BookingID: booking.hotelBookingID,
                        Hotel: booking.hotelName || `Hotel ${booking.hotelID}`,
                        Status: getStatusText(booking.status),
                        Country: booking.country || "",
                        ContactName: booking.contactName || "",
                        ContactEmail: booking.contactEmail || "",
                        RoomNo: roomIndex + 1,
                        RoomType: room.roomTypeName || "",
                        CheckIn: formatDate(room.checkInDate),
                        CheckOut: formatDate(room.checkOutDate),
                        GuestNo: guests.length + i + 1,
                        GuestName: "",
                        Function: "",
                    });
                }
            });
        });

        if (rows.length === 0) {
            notifications.show({
                color: "yellow",
                title: "Ingen data",
                message: "Der er ingen rooming list data at eksportere.",
            });
            return;
        }

        downloadExcel(rows, "RoomingList", "hotel-rooming-list.xlsx");
    }

    function downloadExcel(rows: any[], sheetName: string, fileName: string) {
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        XLSX.writeFile(workbook, fileName);
    }

    return (
        <div>
            <HeroSectionV2
                titleEn="Hotel booking admin"
                titleDa="Hotel booking administration"
                descriptionEn="Approve bookings, follow payment status and view room details."
                descriptionDa="Godkend bookinger, følg betaling og se værelsesdetaljer."
                imageSrc={Billede1}
                badgeText="HOTELS"
            />

            <Container size="xl" py="md">
                <Stack gap="md">
                    <Paper withBorder p="lg" radius="md">
                        <Group justify="space-between" align="flex-end">
                            <Stack gap={2}>
                                <Title order={3}>Hotel bookings</Title>
                                <Text c="dimmed" size="sm">
                                    Godkend, afvis, opret betalingslink og eksporter rooming lists.
                                </Text>
                            </Stack>

                            <Group align="flex-end">
                                <Select
                                    label="Event"
                                    placeholder="Vælg event..."
                                    data={eventSelectData}
                                    value={selectedEventId ? String(selectedEventId) : null}
                                    onChange={(v) => setSelectedEventId(v ? Number(v) : null)}
                                    searchable
                                    w={320}
                                />

                                <Button
                                    variant="light"
                                    leftSection={<IconRefresh size={16} />}
                                    onClick={loadBookings}
                                    disabled={!selectedEventId}
                                >
                                    Refresh
                                </Button>
                            </Group>
                        </Group>
                    </Paper>

                    {selectedEventId && (
                        <>
                            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Bookings</Text>
                                    <Text size="xl" fw={900}>{stats.totalBookings}</Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Rooms</Text>
                                    <Text size="xl" fw={900}>{stats.totalRooms}</Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Awaiting payment</Text>
                                    <Text size="xl" fw={900} c="blue">{stats.awaitingPayment}</Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Missing rooming names</Text>
                                    <Text size="xl" fw={900} c={stats.missingGuests > 0 ? "orange" : "green"}>
                                        {stats.missingGuests}
                                    </Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Pending approval</Text>
                                    <Text size="xl" fw={900} c="yellow">{stats.pendingApproval}</Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Guests</Text>
                                    <Text size="xl" fw={900}>{stats.totalGuests}</Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Revenue</Text>
                                    <Text size="xl" fw={900} c="green">{formatMoney(stats.revenue)}</Text>
                                </Paper>

                                <Paper withBorder p="md" radius="md">
                                    <Text size="xs" c="dimmed">Exports</Text>
                                    <Group gap="xs" mt={6}>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconDownload size={14} />}
                                            onClick={exportBookingsToExcel}
                                        >
                                            Bookings
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            leftSection={<IconFileSpreadsheet size={14} />}
                                            onClick={exportRoomingListToExcel}
                                        >
                                            Rooming
                                        </Button>
                                    </Group>
                                </Paper>
                            </SimpleGrid>

                            <Paper withBorder p="md" radius="md">
                                <Group align="flex-end">
                                    <TextInput
                                        label="Search"
                                        placeholder="Booking, hotel, country, contact, email..."
                                        value={search}
                                        onChange={(e) => setSearch(e.currentTarget.value)}
                                        leftSection={<IconSearch size={16} />}
                                        style={{ flex: 1 }}
                                    />

                                    <Select
                                        label="Status"
                                        data={[
                                            { value: "all", label: "All" },
                                            { value: "PendingApproval", label: "Pending approval" },
                                            { value: "AwaitingPayment", label: "Awaiting payment" },
                                            { value: "Paid", label: "Paid" },
                                            { value: "RoomAssignmentOpen", label: "Rooming list open" },
                                            { value: "Completed", label: "Completed" },
                                            { value: "Rejected", label: "Rejected" },
                                            { value: "Cancelled", label: "Cancelled" },
                                        ]}
                                        value={statusFilter}
                                        onChange={(v) => setStatusFilter(v || "all")}
                                        w={220}
                                    />

                                    <Select
                                        label="Hotel"
                                        data={hotelSelectData}
                                        value={hotelFilter}
                                        onChange={(v) => setHotelFilter(v || "all")}
                                        searchable
                                        w={240}
                                    />

                                    <TextInput
                                        label="Fra dato"
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.currentTarget.value)}
                                        w={160}
                                    />

                                    <TextInput
                                        label="Til dato"
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.currentTarget.value)}
                                        w={160}
                                    />

                                    <Switch
                                        label="Only missing rooming"
                                        checked={onlyMissingRooming}
                                        onChange={(e) => setOnlyMissingRooming(e.currentTarget.checked)}
                                    />
                                </Group>
                            </Paper>

                            <Paper withBorder p="md" radius="md">
                                <Text size="xs" c="dimmed">Room types</Text>

                                <Stack gap={4} mt="xs">
                                    {roomTypeStats.length === 0 ? (
                                        <Text size="sm" c="dimmed">Ingen værelser i valgt periode</Text>
                                    ) : (
                                        roomTypeStats.map((item) => (
                                            <Group key={item.roomType} justify="space-between">
                                                <Text size="sm">{item.roomType}</Text>
                                                <Badge>{item.count}</Badge>
                                            </Group>
                                        ))
                                    )}
                                </Stack>
                            </Paper>

                            <Paper withBorder p="md" radius="md">
                                <Group justify="space-between" mb="sm">
                                    <Title order={4}>Bookede værelser</Title>
                                    <Badge size="lg">{filteredRooms.length} værelser</Badge>
                                </Group>

                                <Table striped withTableBorder>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Booking</Table.Th>
                                            <Table.Th>Hotel</Table.Th>
                                            <Table.Th>Type</Table.Th>
                                            <Table.Th>Check-in</Table.Th>
                                            <Table.Th>Check-out</Table.Th>
                                            <Table.Th>Gæster</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>

                                    <Table.Tbody>
                                        {filteredRooms.map((room, index) => (
                                            <Table.Tr key={`${room.bookingID}-${index}`}>
                                                <Table.Td>#{room.bookingID}</Table.Td>
                                                <Table.Td>{room.hotel}</Table.Td>
                                                <Table.Td>{room.roomType}</Table.Td>
                                                <Table.Td>{formatDate(room.checkIn)}</Table.Td>
                                                <Table.Td>{formatDate(room.checkOut)}</Table.Td>
                                                <Table.Td>{room.guestsCount}</Table.Td>
                                                <Table.Td>
                                                    <Badge color={getStatusColor(room.status)}>
                                                        {getStatusText(room.status)}
                                                    </Badge>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Paper>
                        </>
                    )}

                    {!selectedEventId ? (
                        <Alert color="blue" variant="light">
                            Vælg et event for at se hotelbookinger.
                        </Alert>
                    ) : loading ? (
                        <Paper withBorder p="xl" radius="md">
                            <Group justify="center">
                                <Loader />
                            </Group>
                        </Paper>
                    ) : filteredBookings.length === 0 ? (
                        <Alert color="blue" variant="light">
                            Ingen hotelbookinger fundet.
                        </Alert>
                    ) : (
                        <Stack gap="md">
                            {filteredBookings.map((booking) => {
                                const status = normalizeStatus(booking.status);
                                const isSaving = savingId === booking.hotelBookingID;
                                const progress = getRoomingProgress(booking);

                                return (
                                    <Card key={booking.hotelBookingID} withBorder radius="md" p="lg">
                                        <Group justify="space-between" align="flex-start">
                                            <Stack gap={5} style={{ flex: 1 }}>
                                                <Group>
                                                    <Title order={4}>Booking #{booking.hotelBookingID}</Title>
                                                    <Badge color={getStatusColor(booking.status)}>
                                                        {getStatusText(booking.status)}
                                                    </Badge>
                                                </Group>

                                                <Text size="sm" c="dimmed">
                                                    {booking.hotelName || `Hotel ${booking.hotelID}`} · {booking.country}
                                                </Text>

                                                <Group gap="lg">
                                                    <Text size="sm">
                                                        Contact: <strong>{booking.contactName || "-"}</strong>
                                                    </Text>
                                                    <Text size="sm">
                                                        Email: <strong>{booking.contactEmail || "-"}</strong>
                                                    </Text>
                                                    <Text size="sm">
                                                        Rooms: <strong>{booking.rooms?.length ?? 0}</strong>
                                                    </Text>
                                                    <Text size="sm">
                                                        Guests:{" "}
                                                        <strong>
                                                            {progress.filledGuests}/{progress.totalGuests}
                                                        </strong>
                                                    </Text>
                                                    <Text size="sm">
                                                        Total: <strong>{formatMoney(booking.totalPrice)}</strong>
                                                    </Text>
                                                </Group>

                                                <Text size="xs" c="dimmed">
                                                    Created: {formatDateTime(booking.createdAt)}
                                                    {" · "}
                                                    Rooming deadline: {formatDateTime(booking.roomingDeadline)}
                                                </Text>
                                            </Stack>

                                            <Group justify="flex-end">
                                                <Button
                                                    variant="light"
                                                    leftSection={<IconEye size={16} />}
                                                    onClick={() => openView(booking)}
                                                >
                                                    View
                                                </Button>

                                                {status === "pendingapproval" && (
                                                    <>
                                                        <Button
                                                            color="green"
                                                            loading={isSaving}
                                                            leftSection={<IconCheck size={16} />}
                                                            onClick={() => approveBooking(booking)}
                                                        >
                                                            Approve
                                                        </Button>

                                                        <Button
                                                            color="red"
                                                            variant="light"
                                                            loading={isSaving}
                                                            leftSection={<IconX size={16} />}
                                                            onClick={() => rejectBooking(booking)}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </>
                                                )}

                                                {status === "awaitingpayment" && (
                                                    <>
                                                        <Button
                                                            variant="light"
                                                            leftSection={<IconCreditCard size={16} />}
                                                            onClick={() => createPaymentLink(booking)}
                                                        >
                                                            Payment link
                                                        </Button>

                                                        <Button
                                                            color="green"
                                                            loading={isSaving}
                                                            leftSection={<IconCreditCard size={16} />}
                                                            onClick={() => markPaid(booking)}
                                                        >
                                                            Mark paid
                                                        </Button>
                                                    </>
                                                )}

                                                {(status === "paid" || status === "roomassignmentopen") && (
                                                    <Badge color="violet" size="lg">
                                                        Rooming list
                                                    </Badge>
                                                )}
                                            </Group>
                                        </Group>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </Container>

            <Modal
                opened={viewOpen}
                onClose={() => setViewOpen(false)}
                title={selectedBooking ? `Booking #${selectedBooking.hotelBookingID}` : "Booking"}
                size="xl"
            >
                {selectedBooking && (
                    <Stack gap="md">
                        <Group justify="space-between">
                            <Stack gap={2}>
                                <Title order={4}>
                                    {selectedBooking.hotelName || `Hotel ${selectedBooking.hotelID}`}
                                </Title>
                                <Text c="dimmed" size="sm">
                                    {selectedBooking.country} · {selectedBooking.contactName || "-"} ·{" "}
                                    {selectedBooking.contactEmail || "-"}
                                </Text>
                            </Stack>

                            <Badge color={getStatusColor(selectedBooking.status)} size="lg">
                                {getStatusText(selectedBooking.status)}
                            </Badge>
                        </Group>

                        <Group grow>
                            <Paper withBorder p="md" radius="md">
                                <Text size="xs" c="dimmed">Total</Text>
                                <Text fw={900}>{formatMoney(selectedBooking.totalPrice)}</Text>
                            </Paper>

                            <Paper withBorder p="md" radius="md">
                                <Text size="xs" c="dimmed">Rooms</Text>
                                <Text fw={900}>{selectedBooking.rooms?.length ?? 0}</Text>
                            </Paper>

                            <Paper withBorder p="md" radius="md">
                                <Text size="xs" c="dimmed">Created</Text>
                                <Text fw={900}>{formatDateTime(selectedBooking.createdAt)}</Text>
                            </Paper>
                        </Group>

                        <Table withTableBorder striped>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>#</Table.Th>
                                    <Table.Th>Room type</Table.Th>
                                    <Table.Th>Check-in</Table.Th>
                                    <Table.Th>Check-out</Table.Th>
                                    <Table.Th>Guests</Table.Th>
                                    <Table.Th>Filled names</Table.Th>
                                    <Table.Th>Price / night</Table.Th>
                                    <Table.Th>Total</Table.Th>
                                </Table.Tr>
                            </Table.Thead>

                            <Table.Tbody>
                                {selectedBooking.rooms?.map((room, index) => {
                                    const filled = room.guests?.filter((g) => g.personName?.trim()).length ?? 0;

                                    return (
                                        <Table.Tr key={room.hotelBookingRoomID}>
                                            <Table.Td>{index + 1}</Table.Td>
                                            <Table.Td>{room.roomTypeName}</Table.Td>
                                            <Table.Td>{formatDate(room.checkInDate)}</Table.Td>
                                            <Table.Td>{formatDate(room.checkOutDate)}</Table.Td>
                                            <Table.Td>{room.guestsCount}</Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={filled < (room.guestsCount ?? 0) ? "orange" : "green"}
                                                    variant="light"
                                                >
                                                    {filled}/{room.guestsCount}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>{formatMoney(room.pricePerNight)}</Table.Td>
                                            <Table.Td>{formatMoney(room.totalPrice)}</Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>

                        <Group justify="flex-end">
                            <Button
                                variant="light"
                                leftSection={<IconFileSpreadsheet size={16} />}
                                onClick={exportRoomingListToExcel}
                            >
                                Export current filter
                            </Button>

                            {normalizeStatus(selectedBooking.status) === "pendingapproval" && (
                                <>
                                    <Button
                                        color="green"
                                        leftSection={<IconCheck size={16} />}
                                        onClick={() => approveBooking(selectedBooking)}
                                    >
                                        Approve
                                    </Button>

                                    <Button
                                        color="red"
                                        variant="light"
                                        leftSection={<IconX size={16} />}
                                        onClick={() => rejectBooking(selectedBooking)}
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}

                            {normalizeStatus(selectedBooking.status) === "awaitingpayment" && (
                                <>
                                    <Button
                                        variant="light"
                                        leftSection={<IconCreditCard size={16} />}
                                        onClick={() => createPaymentLink(selectedBooking)}
                                    >
                                        Payment link
                                    </Button>

                                    <Button
                                        color="green"
                                        leftSection={<IconCreditCard size={16} />}
                                        onClick={() => markPaid(selectedBooking)}
                                    >
                                        Mark paid
                                    </Button>
                                </>
                            )}
                        </Group>
                    </Stack>
                )}
            </Modal>
        </div>
    );
}
